import React from "react";
import PropTypes from "prop-types";
import { useFetch } from "./authentication";
import UnauthorizedPage from "../../pages/error/unauthorized-page";

export const PermissionsContext = React.createContext(null);

export const usePermissions = () => {
	return React.useContext(PermissionsContext);
};

export const PermissionsLayer = ({ children }) => {
	const [currentPermissions, setCurrentPermissions] = React.useState(null);
	const [error, setError] = React.useState(null);
	const fetch = useFetch();

	// Get permissions
	const loadRights = React.useCallback(async () => {
		try {
			setError(null);
			const response = await fetch(
				`${window.weezConfig.api.apiUrl}/auth/_self/`
			);

			if (!response.ok) {
				const errorCode = response?.status;
				setError({ code: errorCode });
				return null;
			}

			const { roles } = await response.json();
			const permissions = new Permissions(roles);

			if (!roles || roles.length === 0) {
				setError({ code: 403 });
				return null;
			}

			return permissions;
		} catch (err) {
			const errorCode = err?.status || err?.response?.status || 500;
			const normalizedErrorCode =
				errorCode === 403 || errorCode === 500 ? errorCode : 500;
			setError({ code: normalizedErrorCode });
			return null;
		}
	}, [fetch]);

	React.useEffect(() => {
		loadRights().then(permissions => {
			if (permissions) {
				setCurrentPermissions(permissions);
			}
		});
	}, [loadRights]);

	if (error) {
		return (
			<PermissionsContext.Provider value={currentPermissions}>
				<UnauthorizedPage
					errorCode={error.code}
					refetch={() => {
						loadRights().then(permissions => {
							if (permissions) {
								setCurrentPermissions(permissions);
							}
						});
					}}
				/>
			</PermissionsContext.Provider>
		);
	}

	if (!currentPermissions) {
		return null;
	}

	if (
		currentPermissions &&
		currentPermissions.roles &&
		currentPermissions.roles.length === 0
	) {
		return (
			<PermissionsContext.Provider value={currentPermissions}>
				<UnauthorizedPage
					errorCode={403}
					refetch={() => {
						loadRights().then(permissions => {
							if (permissions) {
								setCurrentPermissions(permissions);
							}
						});
					}}
				/>
			</PermissionsContext.Provider>
		);
	}

	return (
		<PermissionsContext.Provider value={currentPermissions}>
			{children}
		</PermissionsContext.Provider>
	);
};

PermissionsLayer.propTypes = {
	children: PropTypes.node
};

class Permissions {
	constructor(roles) {
		this.roles = roles || [];
	}

	hasRole(role) {
		return Boolean(
			this.roles.find(r => r.role.slug === role.toLowerCase())
		);
	}

	isAdmin() {
		return Boolean(
			this.roles.find(
				r => r.role.slug === "admin" && r.context.level === 0
			)
		);
	}

	hasCapacity(capacity, organization_id) {
		return Boolean(
			this.roles
				// lookup match roles
				.filter(
					r =>
						r.context.level === 0 ||
						(organization_id &&
							r.context.level === 3 &&
							r.context.value === organization_id.toString())
				)
				// find at least one role with the capacity
				.find(r => r.role.capacities.find(c => c.slug === capacity))
		);
	}
	capacityOrga(organization_id) {
		const capacities = [];
		this.roles
			.filter(
				r =>
					r.context.level === 0 ||
					(r.context.level === 3 &&
						r.context.value === organization_id.toString())
			)
			.map(role =>
				role.role.capacities.forEach(capacity =>
					capacities.push(capacity.slug)
				)
			);

		return new Set(capacities);
	}

	get capacities() {
		const capacities = [];
		this.roles.map(role =>
			role.role.capacities.forEach(capacity =>
				capacities.push(capacity.slug)
			)
		);
		return new Set(capacities);
	}

	hasRightsOnOrga(organization_id) {
		return Boolean(
			this.roles.find(r => {
				return (
					r.role.slug !== "minisite" &&
					r.context.level === 3 &&
					r.context.value === organization_id.toString()
				);
			})
		);
	}

	hasPermissionsCrossOrga() {
		//TODO : Remove filter on slug when nBO is open
		return Boolean(
			this.roles.find(
				r => r.context.level === 0 && r.role.slug !== "minisite"
			)
		);
	}

	canAccessNBO(organization_id) {
		const isAdmin = this.isAdmin();
		const hasPermissionsCrossOrga = this.hasPermissionsCrossOrga();
		const hasRightsOnOrga = this.hasRightsOnOrga(organization_id);

		return isAdmin || hasPermissionsCrossOrga || hasRightsOnOrga;
	}

	hasCapacities(capacities, organizationId) {
		const userCapacities = this.capacityOrga(organizationId);
		return Boolean(
			capacities.find(capacity => userCapacities.has(capacity))
		);
	}
}
